import { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Search } from "lucide-react";
import { Input } from "./ui/input";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type { Item } from "@/types/item";
import { Badge } from "./ui/badge";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const SearchStockModal = ({
  isOpen,
  onClose,
  onSelectItem,
  mode = "in", // "in" for stock in, "out" for stock out
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelectItem: (item: Item) => void;
  mode?: "in" | "out";
}) => {
  const { format: formatDecimal } = useDecimalPrecision();
  const [searchTerm, setSearchTerm] = useState("");

  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["items", searchTerm],
    queryFn: async () => {
      const response = await api.get<Item[]>("/items", {
        params: {
          search: searchTerm,
        },
      });
      return response.data;
    },
    select: (data) =>
      data.filter(
        (item) =>
          // Only show stock items (not non-stock)
          item.stockType === "stock" &&
          // Filter by search term
          (item.itemCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    enabled: isOpen,
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handleSelectItem = (item: Item) => {
    // For stock out, check if item has available quantity
    if (mode === "out" && (!item.quantity || item.quantity <= 0)) {
      return; // Don't allow selection if no stock available
    }
    onSelectItem(item);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Search Stock Items</DialogTitle>
          <DialogDescription>
            {mode === "in"
              ? "Search for stock items to add to stock in transaction."
              : "Search for stock items with available quantity for stock out transaction."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by item code or description..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8"
              aria-label="Search items"
            />
          </div>
          <div className="relative border rounded-md">
            <Table>
              <TableHeader className="bg-white dark:bg-gray-950 sticky top-0">
                <TableRow>
                  <TableHead className="w-[150px]">Item Code</TableHead>
                  <TableHead className="w-[250px]">Description</TableHead>
                  <TableHead className="w-[120px] text-center">
                    Stock Quantity
                  </TableHead>
                  <TableHead className="w-[150px] text-right">
                    Weighted Average
                  </TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
            </Table>
            <div className="max-h-[40vh] overflow-auto">
              <Table>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-center text-red-500"
                      >
                        Error loading items
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        No stock items found
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => {
                      const hasStock = (item.quantity || 0) > 0;
                      const isDisabled = mode === "out" && !hasStock;

                      return (
                        <TableRow
                          key={item.id}
                          className={
                            isDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-muted cursor-pointer"
                          }
                        >
                          <TableCell className="w-[150px] font-medium">
                            {item.itemCode}
                          </TableCell>
                          <TableCell className="w-[250px]">
                            {item.description}
                          </TableCell>
                          <TableCell className="w-[120px] text-center">
                            <Badge
                              variant={hasStock ? "default" : "destructive"}
                            >
                              {formatDecimal(item.quantity) || "0.00"}
                            </Badge>
                          </TableCell>
                          <TableCell className="w-[150px] text-right">
                            RM{" "}
                            {formatDecimal(item.weightedAverage) || "0.00"}
                          </TableCell>
                          <TableCell className="w-[100px]">
                            <Button
                              size="sm"
                              onClick={() => handleSelectItem(item)}
                              disabled={isDisabled}
                              aria-label={`Select ${item.description}`}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchStockModal;
